import { chain } from '@leprecoins/effector';
import { querySync } from 'atomic-router';
import { createEvent, createStore, sample } from 'effector';
import { createActivateDeviceModel } from 'features/activate-device';
import { createActivatePromocodeModel } from 'features/activate-promocode';
import { createCancelPromocodeModel } from 'features/cancel-promocode';
import { createCompletePromocodeModel } from 'features/complete-promocode';
import { createDeleteDeviceModel } from 'features/delete-device';
import { createDepositPlayerModel } from 'features/deposit-player';
import { createEditDeviceModel } from 'features/edit-device';
import { createResetPlayerBalanceModel } from 'features/reset-player-balance';
import { createWithdrawPlayerModel } from 'features/withdraw-player';
import { gameAsideModel } from 'entities/game';
import { shiftModel } from 'entities/shift';
import { viewerModel } from 'entities/viewer';
import {
	createGetDevicesQuery,
	Device,
	EntityWithName,
	Player,
	receiveDevice,
	receiveDeviceActivated,
	receiveDeviceDeleted,
	receivePlayers,
	receivePlayersPatched,
} from 'shared/api';
import { controls, gameRoute } from 'shared/routing';

const activateDeviceModel = createActivateDeviceModel(
	gameRoute.$query.map((query) => (query.id !== null ? Number(query.id) : null)),
);

const activatePromocodeModel = createActivatePromocodeModel();

const cancelPromocodeModel = createCancelPromocodeModel();

const completePromocodeModel = createCompletePromocodeModel();

const editDeviceModel = createEditDeviceModel();

const deleteDeviceModel = createDeleteDeviceModel();

const depositPlayerModel = createDepositPlayerModel();

const withdrawPlayerModel = createWithdrawPlayerModel();

const resetBalancePlayerModel = createResetPlayerBalanceModel();

const getDevicesQuery = createGetDevicesQuery(gameAsideModel.$activeGame);

const $devices = createStore<Device[]>([]);

const $players = createStore<Player[]>([]);

const $currency = viewerModel.$cashdesk.map((cashdesk) => cashdesk?.currency ?? null);

const $isEmptyDevices = $devices.map((devices) => devices.length === 0);

const $isEmptyGames = gameAsideModel.$games.map((games) => games.length === 0);

const pushDevice = createEvent<Device>();

const editDevice = createEvent<EntityWithName>();

const { $pending } = getDevicesQuery;

querySync({
	source: { id: gameAsideModel.$activeGame },
	route: shiftModel.activatedGameRoute,
	controls,
});

chain(shiftModel.activatedGameRoute.opened, gameAsideModel.getGamesCashierQuery.start);

sample({
	clock: pushDevice,
	source: $devices,
	fn: (devices, device) => [...devices, device],
	target: $devices,
});

sample({
	clock: editDevice,
	source: $devices,
	fn: (devices, entity) =>
		devices.map((device) => (device.id === entity.id ? { ...device, name: entity.name } : device)),
	target: $devices,
});

sample({
	clock: gameAsideModel.$activeGame,
	filter: (id) => id !== null,
	target: getDevicesQuery.start,
});

sample({
	clock: getDevicesQuery.finished.success,
	fn: ({ result }) => result,
	target: $devices,
});

sample({
	clock: $devices,
	fn: (devices) => devices.map(({ players }) => players).flat(),
	target: $players,
});

sample({
	clock: receiveDevice.success,
	source: $devices,
	fn: (devices, device) =>
		devices.map((dvc) =>
			dvc.id === device.id
				? {
						...dvc,
						name: device.name,
						isActive: device.isActive,
					}
				: dvc,
		),
	target: $devices,
});

sample({
	clock: receivePlayers.success,
	source: $devices,
	fn: (devices, { deviceId, players }) =>
		devices.map((dvc) =>
			dvc.id === deviceId
				? {
						...dvc,
						players: players.map((player) => ({
							...dvc.players.find((p) => p.id === player.id)!,
							...player,
						})),
					}
				: dvc,
		),
	target: $devices,
});

sample({
	clock: receivePlayersPatched.success,
	source: $devices,
	fn: (devices, { deviceId, players: patchedPlayers }) =>
		devices.map((dvc) =>
			dvc.id === deviceId
				? {
						...dvc,
						players: dvc.players.map((player) => {
							const patch = patchedPlayers.find(({ id }) => id === player.id);

							if (!patch) {
								return player;
							}

							let { voucherItem } = player;

							if (patch.voucherItem === null) {
								voucherItem = null;
							} else if (patch.voucherItem !== undefined) {
								voucherItem =
									player.voucherItem === null
										? (patch.voucherItem as Player['voucherItem'])
										: { ...player.voucherItem, ...patch.voucherItem };
							}

							return {
								...player,
								...patch,
								voucherItem,
							};
						}),
					}
				: dvc,
		),
	target: $devices,
});

sample({
	clock: receiveDeviceDeleted.success,
	source: $devices,
	fn: (devices, payload) => devices.filter((dvc) => dvc.id !== payload),
	target: $devices,
});

sample({
	clock: [
		activateDeviceModel.activateDeviceMutation.finished.success.map(({ result }) => result),
		receiveDeviceActivated.success,
	],
	source: $devices,
	filter: (devices, result) => devices.every((device) => device.id !== result.id),
	fn: (_, result) => result,
	target: pushDevice,
});

sample({
	clock: [
		depositPlayerModel.depositPlayerMutation.finished.success,
		withdrawPlayerModel.withdrawPlayerMutation.finished.success,
	],
	fn: ({ result: { shift } }) => shift,
	target: [shiftModel.$shiftPersonal, shiftModel.$shiftTotal],
});

sample({
	clock: activatePromocodeModel.activatePromocodeMutation.finished.success,
	source: $devices,
	fn: (devices, { result }) =>
		devices.map((device) => ({
			...device,
			players: device.players.map((player) =>
				player.id === result.id
					? {
							...player,
							balance: result.balance,
							order: result.order,
							isConnected: result.isConnected,
							name: result.name,
							voucherItem: result.voucherItem,
						}
					: player,
			),
		})),
	target: $devices,
});

sample({
	clock: [
		cancelPromocodeModel.mutation.finished.success,
		completePromocodeModel.mutation.finished.success,
	],
	source: $devices,
	fn: (devices, { result }) =>
		devices.map((device) => ({
			...device,
			players: device.players.map((player) =>
				player.id === result.id
					? {
							...player,
							balance: result.balance,
							order: result.order,
							isConnected: result.isConnected,
							name: result.name,
							voucherItem: null,
						}
					: player,
			),
		})),
	target: $devices,
});

sample({
	clock: editDeviceModel.editDeviceMutation.finished.success,
	source: $devices,
	fn: (devices, { result }) =>
		devices.map((dvc) => (dvc.id === result.id ? { ...dvc, name: result.name } : dvc)),
	target: $devices,
});

sample({
	clock: [
		deleteDeviceModel.mutation.finished.success.map(({ params }) => params),
		receiveDeviceDeleted.success,
	],
	source: $devices,
	fn: (devices, deviceId) => devices.filter((device) => device.id !== deviceId),
	target: $devices,
});

chain(shiftModel.activatedGameRoute.closed, gameAsideModel.$activeGame.reinit);

export const gamePageModel = {
	activateDeviceModel,
	activatePromocodeModel,
	cancelPromocodeModel,
	completePromocodeModel,
	editDeviceModel,
	deleteDeviceModel,
	depositPlayerModel,
	resetBalancePlayerModel,
	withdrawPlayerModel,
	getDevicesQuery,
	$devices,
	$players,
	$currency,
	$pending,
	$isEmptyDevices,
	$isEmptyGames,
	pushDevice,
	editDevice,
};
